import {
  clearFieldValue,
  type IssueFieldValueType,
  shouldRemoveNodeOnDeletion,
} from '@github-ui/issue-fields-shared/issue-field'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {DeleteIssueFieldValueInput} from './__generated__/deleteIssueFieldValueMutation.graphql'
import {ISSUE_FIELD_VALUES_PAGE_SIZE} from './constants'

export function commitDeleteIssueFieldValueMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: DeleteIssueFieldValueInput
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  return commitMutation(environment, {
    mutation: graphql`
      mutation deleteIssueFieldValueMutation($input: DeleteIssueFieldValueInput!) {
        deleteIssueFieldValue(input: $input) {
          success
          issue {
            id
          }
        }
      }
    `,
    variables: {
      input,
    },
    onCompleted: () => {
      onCompleted?.()
    },
    onError: error => onError?.(error),
    optimisticUpdater: store => {
      const issue = store.get(input.issueId)
      if (issue) {
        handleFieldDeletion(issue, input.fieldId)
      }
    },
    updater: store => {
      const issue = store.get(input.issueId)
      if (issue) {
        handleFieldDeletion(issue, input.fieldId)
      }
    },
  })
}

// Helper to handle field deletion logic
const handleFieldDeletion = (issue: ReturnType<RecordSourceSelectorProxy['get']>, fieldId: string) => {
  if (!issue) return
  const fieldValues = issue.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
  if (!fieldValues) return

  const nodes = fieldValues.getLinkedRecords('nodes') || []
  const deletedNode = nodes.find(node => node?.getLinkedRecord('field')?.getDataID() === fieldId)
  if (!deletedNode) return

  const type = deletedNode.getType() as IssueFieldValueType

  if (shouldRemoveNodeOnDeletion(type)) {
    // For text and number fields, remove the node entirely (field disappears from UI)
    const updatedNodes = nodes.filter(node => node?.getLinkedRecord('field')?.getDataID() !== fieldId)
    fieldValues.setLinkedRecords(updatedNodes, 'nodes')
  } else {
    // For single-select and date, clear the value but keep the node (shows 'no value selected')
    clearFieldValue(deletedNode, type)
    // Keep the node in the list
  }
}
